import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Database, Clock, FileBarChart, BarChart3, TrendingUp, Eye, Trash2, Loader2, RotateCcw } from 'lucide-react';
import { formatImportTimestamp } from '@/hooks/use-import-persistence';
import { Auth } from '@/components/Auth';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AutoSaveIndicator } from '@/components/AutoSaveIndicator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface SavedImport {
  id: string;
  imported_at: string;
  updated_at: string;
  type: 'ibkr' | 'revolut' | 'sma';
  summary: string;
  data: any;
}

const CollectedData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [imports, setImports] = useState<SavedImport[]>([]);
  const [selectedImport, setSelectedImport] = useState<SavedImport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('all');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadAllImports();
    }
  }, [user]);

  const loadAllImports = async () => {
    setLoading(true);
    try {
      const allImports: SavedImport[] = [];

      // Load IBKR imports
      const { data: ibkrData, error: ibkrError } = await supabase
        .from('ibkr_imports')
        .select('*')
        .order('imported_at', { ascending: false });

      if (!ibkrError && ibkrData) {
        ibkrData.forEach(item => {
          allImports.push({
            id: item.id,
            imported_at: item.imported_at,
            updated_at: item.updated_at,
            type: 'ibkr',
            summary: `Account: ${item.account_id || 'N/A'} | Period: ${item.statement_period || 'N/A'}`,
            data: item
          });
        });
      }

      // Load Revolut imports
      const { data: revolutData, error: revolutError } = await supabase
        .from('revolut_imports')
        .select('*')
        .order('imported_at', { ascending: false });

      if (!revolutError && revolutData) {
        revolutData.forEach(item => {
          allImports.push({
            id: item.id,
            imported_at: item.imported_at,
            updated_at: item.updated_at,
            type: 'revolut',
            summary: `${item.total_positions || 0} positions | Value: ${item.total_value?.toLocaleString() || '0'} ${item.currency || ''}`,
            data: item
          });
        });
      }

      // Load SMA Surveillance imports
      const { data: smaData, error: smaError } = await supabase
        .from('sma_surveillance_imports')
        .select('*')
        .order('imported_at', { ascending: false });

      if (!smaError && smaData) {
        smaData.forEach(item => {
          allImports.push({
            id: item.id,
            imported_at: item.imported_at,
            updated_at: item.updated_at,
            type: 'sma',
            summary: `${item.total_stocks || 0} stocks | Buy: ${item.buy_signals || 0} | Sell: ${item.sell_signals || 0}`,
            data: item
          });
        });
      }

      // Sort all imports by date
      allImports.sort((a, b) => new Date(b.imported_at).getTime() - new Date(a.imported_at).getTime());
      
      setImports(allImports);
    } catch (error) {
      console.error('Error loading imports:', error);
      toast({
        title: "Error loading data",
        description: "Failed to load saved imports",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewImport = (item: SavedImport) => {
    setSelectedImport(item);
    setDialogOpen(true);
  };

  const handleRestoreImport = (item: SavedImport) => {
    const routeMap = {
      ibkr: '/ibkr-import',
      revolut: '/revolut-import',
      sma: '/sma-surveillance'
    } as const;

    // Navigate to the respective page with state to trigger auto-load
    navigate(routeMap[item.type], { state: { autoLoad: true } });
    
    toast({
      title: "Navigating to analysis",
      description: `Loading your saved ${getTypeLabel(item.type)} data...`
    });
  };

  const handleDeleteImport = async (item: SavedImport) => {
    const tableMap = {
      ibkr: 'ibkr_imports',
      revolut: 'revolut_imports',
      sma: 'sma_surveillance_imports'
    } as const;

    const { error } = await supabase
      .from(tableMap[item.type])
      .delete()
      .eq('id', item.id);

    if (error) {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Deleted",
      description: "Import data has been removed"
    });
    
    loadAllImports();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'ibkr':
        return <TrendingUp className="h-4 w-4" />;
      case 'revolut':
        return <FileBarChart className="h-4 w-4" />;
      case 'sma':
        return <BarChart3 className="h-4 w-4" />;
      default:
        return <Database className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'ibkr':
        return 'IBKR Full Analysis';
      case 'revolut':
        return 'Revolut Import';
      case 'sma':
        return 'IBKR Surveillance';
      default:
        return type;
    }
  };

  const getTypeBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'ibkr':
        return 'default';
      case 'revolut':
        return 'secondary';
      case 'sma':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const filteredImports = activeTab === 'all' 
    ? imports 
    : imports.filter(i => i.type === activeTab);

  const renderDataPreview = (item: SavedImport) => {
    const data = item.data;
    
    if (item.type === 'ibkr') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Account ID</p>
              <p className="font-medium">{data.account_id || 'N/A'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Statement Period</p>
              <p className="font-medium">{data.statement_period || 'N/A'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Cash Balance</p>
              <p className="font-medium">{data.cash_balance?.toLocaleString() || '0'} {data.currency || ''}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Realized P/L</p>
              <p className="font-medium">{data.realized_pl?.toLocaleString() || '0'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Dividends</p>
              <p className="font-medium">{data.total_dividends?.toLocaleString() || '0'}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Fees</p>
              <p className="font-medium">{data.total_fees?.toLocaleString() || '0'}</p>
            </div>
          </div>
          
          {data.positions_data && Array.isArray(data.positions_data) && data.positions_data.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Positions ({data.positions_data.length})</h4>
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Cost Basis</TableHead>
                      <TableHead>Market Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.positions_data.slice(0, 20).map((pos: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{pos.symbol || pos.ticker}</TableCell>
                        <TableCell>{pos.quantity}</TableCell>
                        <TableCell>{pos.costBasis?.toLocaleString() || pos.cost_basis?.toLocaleString()}</TableCell>
                        <TableCell>{pos.marketValue?.toLocaleString() || pos.market_value?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'revolut') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Positions</p>
              <p className="font-medium">{data.total_positions || 0}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Value</p>
              <p className="font-medium">{data.total_value?.toLocaleString() || '0'} {data.currency || ''}</p>
            </div>
          </div>
          
          {data.positions_data && Array.isArray(data.positions_data) && data.positions_data.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Positions ({data.positions_data.length})</h4>
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticker</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.positions_data.slice(0, 20).map((pos: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{pos.ticker}</TableCell>
                        <TableCell>{pos.quantity}</TableCell>
                        <TableCell>{pos.pricePerShare?.toFixed(2)}</TableCell>
                        <TableCell>{pos.totalValue?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
      );
    }

    if (item.type === 'sma') {
      const results = data.analysis_results as
        | {
            smaResults?: any[];
            dailyIchimokuResults?: any[];
            weeklyIchimokuResults?: any[];
            smaSummary?: any;
            dailyIchimokuSummary?: any;
            weeklyIchimokuSummary?: any;
          }
        | null
        | undefined;

      const smaResults = results?.smaResults ?? [];
      const dailyIchimokuResults = results?.dailyIchimokuResults ?? [];
      const weeklyIchimokuResults = results?.weeklyIchimokuResults ?? [];

      return (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Total Stocks</p>
              <p className="font-medium">{data.total_stocks || 0}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">With Signals</p>
              <p className="font-medium">{data.stocks_with_signals || 0}</p>
            </div>
            <div className="p-3 bg-success/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Buy Signals</p>
              <p className="font-medium text-success">{data.buy_signals || 0}</p>
            </div>
            <div className="p-3 bg-destructive/10 rounded-lg">
              <p className="text-sm text-muted-foreground">Sell Signals</p>
              <p className="font-medium text-destructive">{data.sell_signals || 0}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">SMA rows</p>
              <p className="font-medium">{smaResults.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Ichimoku (daily)</p>
              <p className="font-medium">{dailyIchimokuResults.length}</p>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Ichimoku (weekly)</p>
              <p className="font-medium">{weeklyIchimokuResults.length}</p>
            </div>
          </div>

          {smaResults.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">SMA Snapshot (first 20)</h4>
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticker</TableHead>
                      <TableHead>Signal</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">SMA 50</TableHead>
                      <TableHead className="text-right">SMA 200</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {smaResults.slice(0, 20).map((result: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{result.ticker}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              result.signal50_200 === 'BUY' || result.signal2_12 === 'BUY'
                                ? 'default'
                                : result.signal50_200 === 'SELL' || result.signal2_12 === 'SELL'
                                  ? 'destructive'
                                  : 'secondary'
                            }
                          >
                            {result.signal50_200 || result.signal2_12 || 'NEUTRAL'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{result.currentPrice?.toFixed?.(2) ?? '-'}</TableCell>
                        <TableCell className="text-right">{result.sma50?.toFixed?.(2) ?? '-'}</TableCell>
                        <TableCell className="text-right">{result.sma200?.toFixed?.(2) ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              <p className="text-xs text-muted-foreground mt-2">
                Note: charts are fetched on-demand after restore to keep saves fast.
              </p>
            </div>
          )}
        </div>
      );
    }

    return <pre className="text-xs overflow-auto max-h-[300px]">{JSON.stringify(data, null, 2)}</pre>;
  };

  if (!user) {
    return <Auth />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Decorative background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-lg">
                <Database className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Collected Data</h1>
                <p className="text-sm text-muted-foreground">View and manage your saved analyses</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AutoSaveIndicator />
            <ThemeToggle />
          </div>
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all" className="gap-2">
              <Database className="h-4 w-4" />
              All ({imports.length})
            </TabsTrigger>
            <TabsTrigger value="ibkr" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              IBKR ({imports.filter(i => i.type === 'ibkr').length})
            </TabsTrigger>
            <TabsTrigger value="revolut" className="gap-2">
              <FileBarChart className="h-4 w-4" />
              Revolut ({imports.filter(i => i.type === 'revolut').length})
            </TabsTrigger>
            <TabsTrigger value="sma" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Surveillance ({imports.filter(i => i.type === 'sma').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredImports.length === 0 ? (
              <Card className="bg-card/90 backdrop-blur-sm">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Database className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No saved data</h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    Run an analysis from the dashboard to save data here. Your IBKR, Revolut, and Surveillance analyses will appear in this list.
                  </p>
                  <Button className="mt-6" onClick={() => navigate('/')}>
                    Go to Dashboard
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredImports.map(item => (
                  <Card key={`${item.type}-${item.id}`} className="bg-card/90 backdrop-blur-sm hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`p-3 rounded-xl ${
                            item.type === 'ibkr' ? 'bg-primary/10' :
                            item.type === 'revolut' ? 'bg-success/10' :
                            'bg-purple-500/10'
                          }`}>
                            {getTypeIcon(item.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={getTypeBadgeVariant(item.type)}>
                                {getTypeLabel(item.type)}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{item.summary}</p>
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>Saved: {formatImportTimestamp(item.imported_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="default" 
                            size="sm" 
                            onClick={() => handleRestoreImport(item)}
                            className="bg-primary hover:bg-primary/90"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleViewImport(item)}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this import?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the saved {getTypeLabel(item.type)} data from {formatImportTimestamp(item.imported_at)}. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteImport(item)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* View Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedImport && getTypeIcon(selectedImport.type)}
                {selectedImport && getTypeLabel(selectedImport.type)}
              </DialogTitle>
              <DialogDescription>
                {selectedImport && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Saved: {formatImportTimestamp(selectedImport.imported_at)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            {selectedImport && renderDataPreview(selectedImport)}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CollectedData;
